import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Loader2, RefreshCw, AlertCircle, ArrowRight,
  TrendingUp, TrendingDown, Minus, Users, Film, Wallet, Coins, Send, LogOut,
} from "lucide-react";

const _fmtInt = (n) => (typeof n === "number" ? n.toLocaleString() : "—");
const _fmtMoney = (n) => (typeof n === "number" ? `£${n.toFixed(2)}` : "—");
const _fmtWhen = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const TrendPill = ({ delta }) => {
  if (delta > 0) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold"
            style={{ background: "#c0392b1A", color: "#c0392b" }}
            data-testid="combo-nps-trend-up">
        <TrendingUp className="w-3 h-3" /> +{delta} vs prior 24h
      </span>
    );
  }
  if (delta < 0) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold"
            style={{ background: "#27ae601A", color: "#27ae60" }}
            data-testid="combo-nps-trend-down">
        <TrendingDown className="w-3 h-3" /> {delta} vs prior 24h
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold"
          style={{ background: "#8881", color: "#888" }}
          data-testid="combo-nps-trend-flat">
      <Minus className="w-3 h-3" /> flat
    </span>
  );
};

/** Tiny inline SVG sparkline for the 7-day trajectory */
const MiniSparkline = ({ buckets, color = "#FF2D7A" }) => {
  if (!buckets || buckets.length < 2) {
    return <div className="text-xs text-muted-foreground mt-2">Not enough data.</div>;
  }
  const max = Math.max(1, ...buckets);
  const w = 100;
  const h = 24;
  const pts = buckets
    .map((n, i) => {
      const x = (i / (buckets.length - 1)) * w;
      const y = h - (n / max) * (h - 2) - 1;
      return `${x.toFixed(2)},${y.toFixed(2)}`;
    })
    .join(" ");
  const areaPts = `0,${h} ${pts} ${w},${h}`;
  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      preserveAspectRatio="none"
      style={{ width: "100%", height: 36 }}
      aria-hidden="true"
    >
      <polygon points={areaPts} fill={color} fillOpacity="0.12" />
      <polyline
        points={pts}
        fill="none"
        stroke={color}
        strokeWidth="1.5"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  );
};

const SectionLink = ({ to, label, testid }) => (
  <Link
    to={to}
    data-testid={testid}
    className="inline-flex items-center gap-1 text-xs font-semibold text-black/70 hover:text-black"
  >
    {label} <ArrowRight className="w-3 h-3" />
  </Link>
);

const AdminPlyxDashboard = () => {
  const { user, loading: authLoading, logout } = useAuth();
  const navigate = useNavigate();
  const [nps, setNps] = useState(null);
  const [health, setHealth] = useState(null);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [snapping, setSnapping] = useState(false);
  const [snapMsg, setSnapMsg] = useState(null);

  const load = async () => {
    setRefreshing(true);
    try {
      const [npsR, healthR] = await Promise.all([
        api.get("/admin/plyx/nps-stats"),
        api.get("/admin/plyx/health"),
      ]);
      setNps(npsR.data);
      setHealth(healthR.data);
      setError(null);
    } catch (e) {
      setError(e.response?.data?.detail || "Forbidden");
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (authLoading) return; // wait for hydration
    if (!user) {
      // Send unauthenticated visitors to the Plyx-shell admin login —
      // works on both afrimarket.org/admin/plyx and plyx.*/admin/plyx.
      navigate("/plyx/admin/login", { replace: true, state: { from: "/admin/plyx" } });
      return;
    }
    if (user.role !== "admin") {
      // Signed in but not an admin — push back to the public marketplace.
      navigate("/", { replace: true });
      return;
    }
    load();
  }, [user, authLoading, navigate]);

  const snapToSlack = async () => {
    setSnapping(true);
    setSnapMsg(null);
    try {
      const r = await api.post("/admin/plyx/snapshot-to-slack");
      setSnapMsg({
        kind: "ok",
        text: `Posted to Slack · ${r.data.tiles.detractors_24h} detractors · £${(r.data.tiles.outstanding_gbp || 0).toFixed(2)} W2E outstanding.`,
      });
    } catch (e) {
      setSnapMsg({
        kind: "err",
        text: e.response?.data?.detail || "Failed to post to Slack",
      });
    } finally {
      setSnapping(false);
    }
  };

  const handleLogout = async () => {
    try { await logout(); } catch { /* ignore */ }
    navigate("/plyx/admin/login", { replace: true });
  };

  if (error) {
    return (
      <div className="container-narrow py-20 text-center text-muted-foreground" data-testid="combo-error">
        <AlertCircle className="w-8 h-8 mx-auto mb-4 opacity-60" />
        {error}
      </div>
    );
  }
  if (!nps || !health) {
    return (
      <div className="container-narrow py-20 text-center" data-testid="combo-loading">
        <Loader2 className="w-6 h-6 animate-spin mx-auto opacity-60" />
      </div>
    );
  }

  const { last_24h, prior_24h, delta, week_buckets, week_total, recent } = nps;
  const { users, videos, watch_to_earn, trajectory_30d } = health;

  // Today's snapshot from 30-day trajectory (last element)
  const todayUsers = trajectory_30d?.users?.[trajectory_30d.users.length - 1] || 0;
  const todayVideos = trajectory_30d?.videos?.[trajectory_30d.videos.length - 1] || 0;

  return (
    <div className="container-narrow py-10" data-testid="admin-plyx-dashboard-page">
      <div className="flex items-start justify-between mb-8 flex-wrap gap-4">
        <div>
          <h1 className="font-display text-4xl">Plyx · Morning Coffee</h1>
          <p className="text-sm text-muted-foreground mt-1">
            One-glance dashboard for the day. Detractors, growth and W2E balances in a single scroll.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/admin/plyx/nps"
            data-testid="combo-link-nps"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-full border border-black/10 text-sm hover:bg-black/5"
          >
            NPS detail <ArrowRight className="w-4 h-4" />
          </Link>
          <Link
            to="/admin/plyx/health"
            data-testid="combo-link-health"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-full border border-black/10 text-sm hover:bg-black/5"
          >
            Health detail <ArrowRight className="w-4 h-4" />
          </Link>
          <Link
            to="/admin/plyx/audit"
            data-testid="combo-link-audit"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-full border border-black/10 text-sm hover:bg-black/5"
          >
            Audit log <ArrowRight className="w-4 h-4" />
          </Link>
          <button
            onClick={load}
            disabled={refreshing}
            data-testid="combo-refresh-btn"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-full bg-black text-white text-sm hover:opacity-90 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
            Refresh
          </button>
          <button
            onClick={snapToSlack}
            disabled={snapping}
            data-testid="combo-snap-slack-btn"
            title="Post the four hero tiles to the team Slack channel"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-full bg-[#4A154B] text-white text-sm hover:opacity-90 disabled:opacity-50"
          >
            <Send className={`w-4 h-4 ${snapping ? "animate-pulse" : ""}`} />
            {snapping ? "Posting…" : "Snap to Slack"}
          </button>
          <button
            onClick={handleLogout}
            data-testid="combo-logout-btn"
            title="Sign out of the admin console"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-full border border-black/10 text-sm hover:bg-black/5"
          >
            <LogOut className="w-4 h-4" />
            Log out
          </button>
        </div>
      </div>

      {snapMsg ? (
        <div
          data-testid="combo-snap-result"
          className="mb-6 rounded-xl px-4 py-3 text-sm border"
          style={{
            background: snapMsg.kind === "ok" ? "#27ae601A" : "#c0392b1A",
            borderColor: snapMsg.kind === "ok" ? "#27ae6033" : "#c0392b33",
            color: snapMsg.kind === "ok" ? "#1e8449" : "#c0392b",
          }}
        >
          {snapMsg.text}
        </div>
      ) : null}

      {/* TOP ROW — 4 hero tiles */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-10">
        {/* Detractors 24h */}
        <div className="bg-white border border-black/10 rounded-xl p-5" data-testid="combo-tile-detractors">
          <div className="flex items-center justify-between">
            <div className="label-overline text-muted-foreground">Detractors · 24h</div>
            <SectionLink to="/admin/plyx/nps" label="open" testid="combo-detractors-link" />
          </div>
          <div className="font-display text-4xl mt-2 tabular-nums">{last_24h}</div>
          <div className="mt-2"><TrendPill delta={delta} /></div>
          <div className="text-xs text-muted-foreground mt-2">
            Day before: <strong>{prior_24h}</strong> · 7d: <strong>{week_total}</strong>
          </div>
        </div>

        {/* New users today */}
        <div className="bg-white border border-black/10 rounded-xl p-5" data-testid="combo-tile-new-users">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1 label-overline text-muted-foreground">
              <Users className="w-3.5 h-3.5" /> New users · today
            </div>
            <SectionLink to="/admin/plyx/health" label="open" testid="combo-users-link" />
          </div>
          <div className="font-display text-4xl mt-2 tabular-nums">{_fmtInt(todayUsers)}</div>
          <div className="text-xs text-muted-foreground mt-2">
            7d: <strong>{users.new_7d}</strong> · total: <strong>{_fmtInt(users.total)}</strong>
          </div>
        </div>

        {/* New videos today */}
        <div className="bg-white border border-black/10 rounded-xl p-5" data-testid="combo-tile-new-videos">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1 label-overline text-muted-foreground">
              <Film className="w-3.5 h-3.5" /> New videos · today
            </div>
            <SectionLink to="/admin/plyx/health" label="open" testid="combo-videos-link" />
          </div>
          <div className="font-display text-4xl mt-2 tabular-nums">{_fmtInt(todayVideos)}</div>
          <div className="text-xs text-muted-foreground mt-2">
            7d: <strong>{videos.new_7d}</strong> · total: <strong>{_fmtInt(videos.total)}</strong>
          </div>
        </div>

        {/* W2E outstanding balance */}
        <div className="bg-white border border-black/10 rounded-xl p-5" data-testid="combo-tile-w2e">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1 label-overline text-muted-foreground">
              <Wallet className="w-3.5 h-3.5" /> W2E · outstanding
            </div>
            <SectionLink to="/admin/plyx/health" label="open" testid="combo-w2e-link" />
          </div>
          <div className="font-display text-4xl mt-2 tabular-nums">
            {_fmtMoney(watch_to_earn.outstanding_balance_gbp)}
          </div>
          <div className="text-xs text-muted-foreground mt-2">
            Issued: <strong>{_fmtMoney(watch_to_earn.issued_total_gbp)}</strong> ·
            Redeemed: <strong>{_fmtMoney(watch_to_earn.redeemed_total_gbp)}</strong>
          </div>
        </div>
      </div>

      {/* MIDDLE ROW — trajectory sparklines + 7-day NPS bars */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
        <div className="bg-white border border-black/10 rounded-xl p-5" data-testid="combo-trajectory-users">
          <div className="flex items-baseline justify-between">
            <div className="label-overline text-muted-foreground">Users · 30d</div>
            <SectionLink to="/admin/plyx/health" label="detail" testid="combo-spark-users-link" />
          </div>
          {trajectory_30d?.users ? (
            <MiniSparkline buckets={trajectory_30d.users} color="#FF2D7A" />
          ) : (
            <div className="text-xs text-muted-foreground mt-2">No data.</div>
          )}
        </div>
        <div className="bg-white border border-black/10 rounded-xl p-5" data-testid="combo-trajectory-videos">
          <div className="flex items-baseline justify-between">
            <div className="label-overline text-muted-foreground">Videos · 30d</div>
            <SectionLink to="/admin/plyx/health" label="detail" testid="combo-spark-videos-link" />
          </div>
          {trajectory_30d?.videos ? (
            <MiniSparkline buckets={trajectory_30d.videos} color="#00E0C7" />
          ) : (
            <div className="text-xs text-muted-foreground mt-2">No data.</div>
          )}
        </div>
        <div className="bg-white border border-black/10 rounded-xl p-5" data-testid="combo-trajectory-nps">
          <div className="flex items-baseline justify-between">
            <div className="label-overline text-muted-foreground">Detractors · 7d</div>
            <SectionLink to="/admin/plyx/nps" label="detail" testid="combo-spark-nps-link" />
          </div>
          {week_buckets ? (
            <MiniSparkline buckets={week_buckets} color="#FFB42D" />
          ) : (
            <div className="text-xs text-muted-foreground mt-2">No data.</div>
          )}
        </div>
      </div>

      {/* BOTTOM — most recent detractors (top 5) */}
      <div className="bg-white border border-black/10 rounded-xl overflow-hidden" data-testid="combo-recent-detractors">
        <div className="px-6 py-4 border-b border-black/5 flex items-center justify-between">
          <h2 className="font-semibold flex items-center gap-2">
            <Coins className="w-4 h-4 opacity-60" /> Top 5 recent detractors
          </h2>
          <SectionLink to="/admin/plyx/nps" label="see all" testid="combo-recent-see-all" />
        </div>
        {(!recent || recent.length === 0) ? (
          <div className="px-6 py-10 text-center text-muted-foreground text-sm" data-testid="combo-no-detractors">
            No detractors yet — that&rsquo;s a good thing.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-black/5">
                <tr>
                  <th className="px-4 py-2 text-left text-xs uppercase tracking-wider text-muted-foreground" style={{ width: 70 }}>Score</th>
                  <th className="px-4 py-2 text-left text-xs uppercase tracking-wider text-muted-foreground">Reason</th>
                  <th className="px-4 py-2 text-left text-xs uppercase tracking-wider text-muted-foreground" style={{ width: 130 }}>When</th>
                </tr>
              </thead>
              <tbody>
                {recent.slice(0, 5).map((r) => (
                  <tr key={r.feedback_id} className="border-t border-black/5" data-testid={`combo-detractor-row-${r.feedback_id}`}>
                    <td className="px-4 py-3 tabular-nums">{r.score}/5</td>
                    <td className="px-4 py-3">{r.reason || <span className="text-muted-foreground">—</span>}</td>
                    <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">{_fmtWhen(r.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <p className="text-xs text-muted-foreground mt-4">
        NPS generated at {_fmtWhen(nps.generated_at)} · Health generated at {_fmtWhen(health.generated_at)}
      </p>
    </div>
  );
};

export default AdminPlyxDashboard;
